// src/utils/feedHelpers.js
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection, query, where, getDocs, writeBatch, doc, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

/* ================================================================== */
/* Config                                                              */
/* ================================================================== */

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif',
];
export const ALLOWED_VIDEO_TYPES = [
  'video/mp4', 'video/webm', 'video/quicktime',
];
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;     // 5 MB
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;    // 50 MB

/**
 * No hard cap on the number of media items per post or story.
 * Kept as a finite number so code like `MAX_POST_PHOTOS - files.length`
 * behaves predictably without going negative.
 */
export const MAX_POST_PHOTOS = Number.MAX_SAFE_INTEGER;
export const MAX_STORY_PHOTOS = Number.MAX_SAFE_INTEGER;

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

export const sanitizeFilename = (name = 'file') =>
  String(name).replace(/[^\w.\-]+/g, '_').slice(0, 120);

export const isVideoFile = (file) =>
  file && ALLOWED_VIDEO_TYPES.includes(file.type);

export const validateImage = (file) => {
  if (!file) return 'No file';
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return `${file.name}: use JPEG, PNG, WEBP or GIF.`;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `${file.name} exceeds 5MB.`;
  }
  return null;
};

export const validateVideo = (file) => {
  if (!file) return 'No file';
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return `${file.name}: use MP4, WebM or MOV.`;
  }
  if (file.size > MAX_VIDEO_BYTES) {
    return `${file.name} exceeds 50MB.`;
  }
  return null;
};

export const validateMedia = (file) =>
  isVideoFile(file) ? validateVideo(file) : validateImage(file);

export const uploadImage = async (file, folder) => {
  const path = `${folder}/${Date.now()}_${sanitizeFilename(file.name)}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

export const uploadMedia = uploadImage; // same logic

/* ================================================================== */
/* Author sync — keep denormalized author fields on posts in sync      */
/* ================================================================== */

/**
 * Resolve a display name from a raw user document.
 * Handles `name` → `displayName` → email prefix fallback.
 */
export const resolveUserName = (u) => {
  if (!u) return '';
  if (typeof u.name === 'string' && u.name.trim()) return u.name.trim();
  if (typeof u.displayName === 'string' && u.displayName.trim()) {
    return u.displayName.trim();
  }
  if (typeof u.email === 'string' && u.email.includes('@')) {
    return u.email.split('@')[0];
  }
  return '';
};

/**
 * Update denormalized author fields (`authorName`, `authorPhoto`) across
 * every post owned by `uid`. Called after the user changes their display
 * name or profile photo so the feed reflects the change without a reload.
 *
 * Best-effort: failures are logged but do not throw. Returns the number
 * of posts updated (or 0 on failure / no change).
 *
 * @param {string} uid
 * @param {{ authorName?: string, authorPhoto?: string|null }} fields
 * @param {{ skipIfUnchanged?: boolean }} [opts]
 */
export const syncAuthorFieldsOnPosts = async (uid, fields, opts = {}) => {
  if (!uid || !fields || typeof fields !== 'object') return 0;
  const { skipIfUnchanged = true } = opts;

  // Sanitize input — only allow the fields we actually denormalize
  const next = {};
  if (typeof fields.authorName === 'string') {
    next.authorName = fields.authorName;
  }
  if (fields.authorPhoto === null || typeof fields.authorPhoto === 'string') {
    next.authorPhoto = fields.authorPhoto;
  }
  if (Object.keys(next).length === 0) return 0;

  try {
    const postsRef = collection(db, 'posts');
    const snap = await getDocs(query(postsRef, where('authorId', '==', uid)));
    if (snap.empty) return 0;

    // Filter to only posts that actually need updating
    const toUpdate = [];
    snap.forEach((d) => {
      const data = d.data() || {};
      const needsUpdate = Object.entries(next).some(([k, v]) => {
        if (!skipIfUnchanged) return true;
        // Treat null and undefined as the same "missing" value
        const existing = data[k] ?? null;
        const incoming = v ?? null;
        return existing !== incoming;
      });
      if (needsUpdate) toUpdate.push(d);
    });

    if (toUpdate.length === 0) return 0;

    // Firestore batch limit is 500 — chunk to be safe
    const CHUNK = 450;
    let updated = 0;
    for (let i = 0; i < toUpdate.length; i += CHUNK) {
      const batch = writeBatch(db);
      toUpdate.slice(i, i + CHUNK).forEach((d) => {
        batch.update(d.ref, next);
      });
      await batch.commit();
      updated += Math.min(CHUNK, toUpdate.length - i);
    }

    return updated;
  } catch (err) {
    console.warn('syncAuthorFieldsOnPosts failed:', err);
    return 0;
  }
};

/**
 * One-shot: fetch a user's current photo/name from `users/{uid}` and
 * push it to all of their posts. Useful when a component isn't sure
 * whether the caller already updated the user doc, or for a "resync"
 * admin button.
 */
export const resyncAuthorFromUserDoc = async (uid) => {
  if (!uid) return 0;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return 0;
    const u = snap.data() || {};
    return await syncAuthorFieldsOnPosts(uid, {
      authorName: resolveUserName(u) || 'User',
      authorPhoto: u.photoUrl ?? null,
    });
  } catch (err) {
    console.warn('resyncAuthorFromUserDoc failed:', err);
    return 0;
  }
};

/* ================================================================== */
/* Formatting                                                          */
/* ================================================================== */

export const formatRelativeTime = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w`;
  return d.toLocaleDateString();
};

export const formatCount = (n) => {
  if (!n) return '0';
  if (n < 1000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}K`;
  return `${(n / 1000000).toFixed(1)}M`;
};

/* ================================================================== */
/* Media normalization                                                 */
/* ================================================================== */

/**
 * Normalize media array from a post — handles legacy `photos` array too.
 * Returns array of { url, type, isCover }
 */
export const normalizeMedia = (post) => {
  if (!post) return [];

  // New format with cover flags
  if (Array.isArray(post.media) && post.media.length) {
    const hasCover = post.media.some((m) => m.isCover);
    return post.media.map((m, i) => ({
      url: m.url,
      type: m.type || 'image',
      isCover: hasCover ? !!m.isCover : i === 0,
    }));
  }

  // Legacy: photos string array
  if (Array.isArray(post.photos) && post.photos.length) {
    return post.photos.map((url, i) => ({
      url,
      type: 'image',
      isCover: i === 0,
    }));
  }

  return [];
};

/**
 * Same as normalizeMedia but for stories.
 * Stories use `items` array; falls back to legacy `photos`.
 */
export const normalizeStoryMedia = (story) => {
  if (!story) return [];

  if (Array.isArray(story.items) && story.items.length) {
    const hasCover = story.items.some((i) => i.isCover);
    return story.items.map((i, idx) => ({
      url: i.url,
      type: i.type || 'image',
      isCover: hasCover ? !!i.isCover : idx === 0,
    }));
  }

  if (Array.isArray(story.photos) && story.photos.length) {
    return story.photos.map((url, idx) => ({
      url,
      type: 'image',
      isCover: idx === 0,
    }));
  }

  return [];
};

/* ================================================================== */
/* Time helpers                                                        */
/* ================================================================== */

/** Returns ms remaining until expiresAt, or 0 if expired */
export const msUntilExpiry = (expiresAt) => {
  if (!expiresAt) return 0;
  const ts = expiresAt?.toDate?.()?.getTime?.() ?? new Date(expiresAt).getTime();
  return Math.max(0, ts - Date.now());
};

/** Returns human "5h left" style string */
export const formatTimeLeft = (expiresAt) => {
  const ms = msUntilExpiry(expiresAt);
  if (ms <= 0) return 'Expired';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h >= 1) return `${h}h left`;
  if (m >= 1) return `${m}m left`;
  return `${s}s left`;
};

/* ================================================================== */
/* Story defaults                                                      */
/* ================================================================== */

export const STORY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/* ================================================================== */
/* Post ordering helper                                                */
/* ================================================================== */

/**
 * Sort key for a post — uses `postDate` (admin-set) with `createdAt` fallback.
 */
export const getPostTime = (post) =>
  post?.postDate?.toDate?.()?.getTime?.() ||
  post?.createdAt?.toDate?.()?.getTime?.() ||
  0;

/* ================================================================== */
/* Post design — background presets                                    */
/* ================================================================== */

export const POST_BG_PRESETS = [
  { id: 'default', label: 'Default', bg: '', text: '' },
  {
    id: 'orange',
    label: 'Sunset',
    bg: 'bg-gradient-to-br from-orange-400 to-orange-600',
    text: 'text-white',
  },
  {
    id: 'purple',
    label: 'Violet',
    bg: 'bg-gradient-to-br from-purple-500 to-pink-500',
    text: 'text-white',
  },
  {
    id: 'ocean',
    label: 'Ocean',
    bg: 'bg-gradient-to-br from-blue-500 to-cyan-400',
    text: 'text-white',
  },
  {
    id: 'forest',
    label: 'Forest',
    bg: 'bg-gradient-to-br from-green-500 to-emerald-600',
    text: 'text-white',
  },
  {
    id: 'night',
    label: 'Night',
    bg: 'bg-gradient-to-br from-gray-800 to-black',
    text: 'text-white',
  },
  {
    id: 'rose',
    label: 'Rose',
    bg: 'bg-gradient-to-br from-pink-400 to-rose-500',
    text: 'text-white',
  },
  {
    id: 'sky',
    label: 'Sky',
    bg: 'bg-gradient-to-br from-sky-300 to-indigo-400',
    text: 'text-white',
  },
];

/* ================================================================== */
/* Post design — font presets                                          */
/* ================================================================== */

export const FONT_FAMILIES = [
  { id: 'sans', label: 'Sans', className: 'font-sans' },
  { id: 'serif', label: 'Serif', className: 'font-serif' },
  { id: 'mono', label: 'Mono', className: 'font-mono' },
  { id: 'display', label: 'Display', className: 'font-display' },
  { id: 'cursive', label: 'Cursive', className: 'font-cursive' },
  { id: 'rounded', label: 'Rounded', className: 'font-rounded' },
];

export const FONT_SIZES = [
  { id: 'sm', label: 'S', className: 'text-sm' },
  { id: 'base', label: 'M', className: 'text-base' },
  { id: 'lg', label: 'L', className: 'text-lg' },
  { id: 'xl', label: 'XL', className: 'text-xl' },
  { id: '2xl', label: 'XXL', className: 'text-2xl' },
];

export const FONT_WEIGHTS = [
  { id: 'normal', label: 'Normal', className: 'font-normal' },
  { id: 'medium', label: 'Medium', className: 'font-medium' },
  { id: 'semibold', label: 'Semibold', className: 'font-semibold' },
  { id: 'bold', label: 'Bold', className: 'font-bold' },
];

export const TEXT_ALIGNS = [
  { id: 'left', label: 'Left', icon: 'fa-align-left', className: 'text-left' },
  { id: 'center', label: 'Center', icon: 'fa-align-center', className: 'text-center' },
  { id: 'right', label: 'Right', icon: 'fa-align-right', className: 'text-right' },
];

/** Default font object for a new post */
export const DEFAULT_FONT = {
  family: 'sans',
  size: 'xl',
  weight: 'semibold',
  italic: false,
  align: 'center',
};

/**
 * Combine font settings into a single className string.
 * Accepts a font object or undefined/null (returns a sensible default).
 */
export const getFontClass = (font) => {
  if (!font) return 'text-xl font-semibold text-center';

  const family = FONT_FAMILIES.find((f) => f.id === font.family)?.className || '';
  const size = FONT_SIZES.find((s) => s.id === font.size)?.className || 'text-xl';
  const weight =
    FONT_WEIGHTS.find((w) => w.id === font.weight)?.className || 'font-semibold';
  const italic = font.italic ? 'italic' : '';
  const align =
    TEXT_ALIGNS.find((a) => a.id === font.align)?.className || 'text-center';

  return [family, size, weight, italic, align].filter(Boolean).join(' ');
};

/* ================================================================== */
/* Design validation                                                   */
/* ================================================================== */

/**
 * Check if a post has any custom design applied.
 * Useful for showing a design indicator in the composer.
 */
export const hasDesignApplied = (design) => {
  if (!design) return false;
  if (design.bgPreset && design.bgPreset !== 'default') return true;
  if (design.font && design.font.family && design.font.family !== 'sans') return true;
  if (design.font && design.font.italic) return true;
  return false;
};

/* ================================================================== */
/* URL linkify helper (shared by PostCard, Preview, etc.)             */
/* ================================================================== */

export const URL_REGEX =
  /((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,!?;:'"]|[a-z0-9.-]+\.(?:com|org|net|io|gov|edu|co|ph|sa|uk|us|info|biz|dev|app|xyz)(?:\/[^\s<>()]*)?)/gi;

/**
 * Returns true if a given string is a URL.
 * (Useful inside .map() loops with the URL_REGEX constant.)
 */
export const isUrlString = (str) => {
  if (!str) return false;
  URL_REGEX.lastIndex = 0;
  const result = URL_REGEX.test(str);
  URL_REGEX.lastIndex = 0;
  return result;
};

/* ================================================================== */
/* Time-of-day greeting helper                                         */
/* ================================================================== */

export const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};